// Pure reconciliation reducer for classified free-text questions (WP-D3).
//
// The dbam classification endpoint returns one row per response with a
// `decision ∈ {"assigned","other","ambiguous"}`. Every response carries a
// mass of exactly 1 that must land SOMEWHERE — the whole point of this
// reducer is that "n always reconciles": the sum of everything we distribute
// equals the row count exactly. Nothing is ever silently dropped.
//
// Inputs:
//   rows              — classification rows from fetchClassification(...)
//   categories        — array where categories[i].summary is the label; index i
//                       aligns with top_index / assigned_category_index /
//                       similarity[] / fractional_mass keys.
//   labelsBySession   — Map (or plain object) session_id → label string. A label
//                       names its category by identity, or is the literal
//                       'other', or is a bare position for descriptor
//                       categories and labels written before ids existed; see
//                       categoryLabels.js. Author judgments override the row's
//                       own decision so the chart reflects human review.
//
// Output:
//   { perCategory:[{index,summary,solid,faded}], other, ambiguous,
//     unaccounted, n, reconciledMass }
//
// `n` here is the CATEGORIZED count `c` in the UI — every answer with a
// category outcome, whoever decided it: a classifier row (assigned, other, or
// ambiguous) or an author assignment. An author assignment counts even when the
// classifier never saw that answer and it therefore has no classification row.
// It is NOT the number of answers to the question; that is counted from the
// responses themselves.
//
// INVARIANT: Σ_buckets(solid+faded) + other + unaccounted === n exactly, and
// reconciledMass === n. Each unit contributes a total mass of exactly 1; every
// ambiguous row's fractional_mass is normalized to sum to 1 so the n-line is an
// exact integer.
//
// UI-FREE: no React imports, no DOM. Safe to unit-test in isolation.

import { labelToIndex } from './categoryLabels';

// Iterate session→label pairs from either a Map or a plain object.
function forEachLabel(labelsBySession, visit) {
    if (!labelsBySession) return;
    if (typeof labelsBySession.forEach === 'function' && typeof labelsBySession.get === 'function') {
        labelsBySession.forEach((label, sessionId) => visit(sessionId, label));
        return;
    }
    Object.keys(labelsBySession).forEach(sessionId => visit(sessionId, labelsBySession[sessionId]));
}

// An assignment names its category by identity; `categories` resolves it to the
// position the buckets are keyed by. A label naming a category that no longer
// exists resolves to null and is accounted as unaccounted mass, never silently
// read as position 0.
function resolveLabel(labelsBySession, sessionId, categories) {
    return labelToIndex(categories, readLabel(labelsBySession, sessionId));
}

function readLabel(labelsBySession, sessionId) {
    if (sessionId == null) return undefined;
    if (!labelsBySession) return undefined;
    if (typeof labelsBySession.get === 'function') {
        return labelsBySession.get(sessionId);
    }
    if (Object.prototype.hasOwnProperty.call(labelsBySession, sessionId)) {
        return labelsBySession[sessionId];
    }
    return undefined;
}

export function reconcileClassification(rows, categories, labelsBySession) {
    const cats = Array.isArray(categories) ? categories : [];

    // perCategory is keyed by index in a map first so we can lazily expand to
    // cover any index a row references even when categories[] is shorter (or
    // empty). Nothing referenced is ever dropped.
    const byIndex = new Map();
    const ensureBucket = (i) => {
        if (!byIndex.has(i)) {
            const cat = cats[i];
            const summary = (cat && cat.summary != null && cat.summary !== '')
                ? cat.summary
                : `Category ${i}`;
            byIndex.set(i, { index: i, summary, solid: 0, faded: 0 });
        }
        return byIndex.get(i);
    };

    // Seed buckets for every authored category so they render even at zero.
    for (let i = 0; i < cats.length; i++) ensureBucket(i);

    let other = 0;
    let ambiguous = 0;      // pending-review indicator (NOT an extra mass term)
    let unaccounted = 0;
    const rowList = Array.isArray(rows) ? rows : [];

    // An author assignment is a categorization in its own right, so an answer
    // the classifier never saw still counts once the author places it. Those
    // sessions have no classification row, so they are added here as
    // label-only units and carry the same mass of exactly 1.
    const rowSessions = new Set(rowList.map(row => row && row.session_id).filter(sessionId => sessionId != null));
    const labelOnlySessions = [];
    forEachLabel(labelsBySession, (sessionId, label) => {
        if (label === undefined || label === null || label === '') return;
        if (rowSessions.has(sessionId)) return;
        labelOnlySessions.push(sessionId);
    });

    // c: every answer with a category outcome, whoever decided it.
    const n = rowList.length + labelOnlySessions.length;

    // An index is usable when it's a non-negative integer AND either there is
    // no authored schema to validate against (cats empty → trust the data and
    // expand) or it falls within the authored category count. An index past the
    // end of a known schema is "out of range" and spills to unaccounted rather
    // than inventing a phantom bucket.
    const isValidIndex = (i) =>
        Number.isInteger(i) && i >= 0 && (cats.length === 0 || i < cats.length);

    rowList.forEach((row) => {
        if (!row || typeof row !== 'object') {
            // Malformed row still carries a mass of 1 — account for it.
            unaccounted += 1;
            return;
        }

        const label = resolveLabel(labelsBySession, row.session_id, cats);

        // (1) Judged row — an author label resolves it, overriding the decision.
        if (label !== null) {
            if (label === 'other') {
                other += 1;
                return;
            }
            if (isValidIndex(label)) {
                ensureBucket(label).solid += 1;
                return;
            }
            // Unresolvable label — don't drop the mass.
            unaccounted += 1;
            return;
        }

        const decision = row.decision;

        // (2) assigned (unjudged) → +1 solid to the assigned bucket.
        if (decision === 'assigned') {
            const idx = Number(row.assigned_category_index);
            if (isValidIndex(idx)) {
                ensureBucket(idx).solid += 1;
            } else {
                unaccounted += 1;
            }
            return;
        }

        // (3) other (unjudged) → +1 Other.
        if (decision === 'other') {
            other += 1;
            return;
        }

        // (4) ambiguous (unjudged) → distribute fractional_mass across FADED
        //     segments and flag it pending review. The same row is BOTH a mass
        //     distribution AND a pending indicator — the indicator is not extra
        //     mass.
        if (decision === 'ambiguous') {
            const fm = row.fractional_mass;
            if (fm && typeof fm === 'object') {
                const entries = Object.keys(fm)
                    .map((k) => [Number(k), Number(fm[k])])
                    .filter(([i, m]) => isValidIndex(i) && Number.isFinite(m) && m > 0);
                const totalMass = entries.reduce((s, [, m]) => s + m, 0);
                if (entries.length > 0 && totalMass > 0) {
                    // Normalize so this row contributes exactly 1 to Σ — the
                    // n-line stays an exact integer even if upstream masses
                    // drift from summing to 1.
                    entries.forEach(([i, m]) => {
                        ensureBucket(i).faded += m / totalMass;
                    });
                    ambiguous += 1;
                    return;
                }
            }
            // Ambiguous without usable fractional_mass — don't silently drop.
            unaccounted += 1;
            return;
        }

        // (5) Unknown / missing decision — defensive default.
        unaccounted += 1;
    });

    // Author assignments with no classifier row: resolved purely by the label.
    labelOnlySessions.forEach((sessionId) => {
        const label = resolveLabel(labelsBySession, sessionId, cats);
        if (label === 'other') {
            other += 1;
            return;
        }
        if (isValidIndex(label)) {
            ensureBucket(label).solid += 1;
            return;
        }
        unaccounted += 1;
    });

    // Emit perCategory ordered by index for stable, predictable output.
    const perCategory = Array.from(byIndex.values()).sort((a, b) => a.index - b.index);

    const bucketMass = perCategory.reduce((s, c) => s + c.solid + c.faded, 0);
    const reconciledMass = bucketMass + other + unaccounted;

    return { perCategory, other, ambiguous, unaccounted, n, reconciledMass };
}

export default reconcileClassification;
